import {
  keepPreviousData,
  useMutation,
  UseMutationOptions,
  useQuery,
} from "@tanstack/react-query"
import type { InformationRequestStatus } from "@tfg/db"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"

import { SupplierResponses } from "@tfg/types/endpoints/supplier"
import {
  TraceabilityRequests,
  TraceabilityResponses,
} from "@tfg/types/endpoints/traceability"
import {
  updateOperationsValidationType,
  updateOperationValidationType,
} from "@tfg/types/schemas/modules/traceability/queries/find-batch-update-operations.request.dto"
import { transferProdsiteFieldValidationType } from "@tfg/types/schemas/prodSiteFieldRequests"
import { getTraceabilityRequestsQueryParams } from "@tfg/types/schemas/traceabilityRequests"

import { httpClient } from "~/backend/axios.ts"
import { queryClient } from "~/backend/queryClient.ts"
import { findOperationTreeByProductIdsQueryKey } from "~/gen/api"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

export const useListTraceabilityRequests = (
  params: getTraceabilityRequestsQueryParams
) => {
  return useQuery({
    queryKey: ["listTraceabilityRequests", params],
    queryFn: async () =>
      (
        await httpClient.get<TraceabilityResponses.GetTraceabilityRequestsResponse>(
          "/traceability-requests",
          {
            params,
          }
        )
      ).data,
  })
}

export const useSubmitRequestsMutation = (
  options?: UseMutationOptions<
    void,
    unknown,
    {
      requests: TraceabilityRequests.BatchTraceabiityRequest[]
    }
  >
) => {
  return useMutation({
    ...options,
    mutationFn: async ({ requests }) => {
      await httpClient.put(`/traceability-requests/`, requests)
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)

      await queryClient.invalidateQueries({
        queryKey: ["listTraceabilityRequests"],
      })
      await queryClient.invalidateQueries({
        queryKey: ["listCertificates"],
      })
    },
  })
}

export const useUpdateOperationMutation = (
  id: number | undefined,
  options?: UseMutationOptions<
    unknown,
    AxiosError<string>,
    updateOperationValidationType
  >
) => {
  const { t } = useTranslation("traceability")
  return useMutation({
    ...options,
    mutationFn: async (update) => {
      if (!id) {
        throw new Error("Operation id is required")
      }
      return await httpClient.patch(`/operations/${id}`, update)
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      options?.onError?.(error, variables, onMutateResult, context)
      showAxiosError({
        showNotification: true,
        error,
        notificationTitle: t("errors.title"),
      })
    },
  })
}

export const useUpdateOperationsMutation = (
  requestId: number,
  options?: UseMutationOptions<
    TraceabilityResponses.UpdateOperationResponse,
    AxiosError,
    updateOperationsValidationType
  >
) => {
  const { t } = useTranslation("traceability")
  return useMutation({
    ...options,
    mutationFn: async (updates) => {
      const response = await httpClient.put(`/operations`, updates)
      return response.data
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["listTraceabilityRequests", requestId],
          refetchType: "none",
        }),
        queryClient.invalidateQueries({
          queryKey: ["productionSiteFieldRequests"],
        }),
        queryClient.invalidateQueries({
          queryKey: findOperationTreeByProductIdsQueryKey({
            productIds: [],
          }),
        }),
      ])
      showSuccessNotification()
    },

    onError: (error, variables, onMutateResult, context) => {
      options?.onError?.(error, variables, onMutateResult, context)
      showAxiosError({
        notificationTitle: t("errors.title"),
        notificationMessage: t("errors.axiosMessage", {
          message: error.response?.data,
        }),
        showNotification: true,
        error,
      })
    },
  })
}

export const useProductionSiteFieldRequestsQuery = () => {
  return useQuery({
    queryKey: ["productionSiteFieldRequests"],
    queryFn: async () =>
      (
        await httpClient.get<SupplierResponses.ProdsitefieldRequest[]>(
          `v1/production-site-request/find-production-site-request-by-user`
        )
      ).data,
  })
}

export const useReopenRequestMutation = (
  options?: UseMutationOptions<
    TraceabilityResponses.ReopenRequest,
    unknown,
    {
      requestId: number
      mode: "clean" | "dirty"
    }
  >
) => {
  return useMutation({
    ...options,
    mutationFn: async (values) =>
      (
        await httpClient.patch(
          `admin/traceability-requests/${values.requestId}/reopen`,
          { mode: values.mode }
        )
      ).data,
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      await queryClient.invalidateQueries({
        queryKey: ["listTraceabilityRequests"],
      })
    },
  })
}

export const useSetStatusRequestMutation = (
  options?: UseMutationOptions<
    TraceabilityResponses.ReopenRequest,
    unknown,
    { requestId: number; status: InformationRequestStatus }
  >
) => {
  return useMutation({
    ...options,
    mutationFn: async ({ requestId, status }) =>
      (
        await httpClient.patch(
          `admin/traceability-requests/${requestId}/status?status=${status}`
        )
      ).data,
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      showSuccessNotification({
        title: "Status updated",
        message: `The status of the request has been updated to ${variables.status}`,
      })
      await queryClient.invalidateQueries({
        queryKey: ["listTraceabilityRequests"],
      })
    },
    onError: (error, variables, onMutateResult, context) => {
      options?.onError?.(error, variables, onMutateResult, context)
      showAxiosError({
        showNotification: true,
        error: error as Error,
        notificationTitle: "Error updating status",
      })
    },
  })
}

export const useGetTraceabilityRequest = (
  id: number,
  initialData: TraceabilityResponses.TraceabilityRequest
) => {
  return useQuery({
    queryFn: async () => {
      const res =
        await httpClient.get<TraceabilityResponses.TraceabilityRequest>(
          `traceability-requests/${id}`
        )
      return res.data
    },
    queryKey: ["listTraceabilityRequests", id],
    initialData,
    placeholderData: keepPreviousData,
  })
}

export const useSetProdsiteFieldValueMutation = (
  prodsiteId: string,
  prodsiteFieldDefId: string,
  options?: UseMutationOptions<void, unknown, string>
) => {
  return useMutation({
    ...options,
    mutationFn: async (value) => {
      await httpClient.put(
        `/prodsitefield-requests/${prodsiteId}/prodsite-field/${prodsiteFieldDefId}`,
        { value }
      )
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      await queryClient.invalidateQueries({
        queryKey: ["productionSiteFieldRequests"],
      })
    },
  })
}

export const useSubmitProdsiteFieldRequestMutation = (
  prodsiteId: string,
  options?: UseMutationOptions<void, unknown>
) => {
  return useMutation({
    ...options,
    mutationFn: async () => {
      await httpClient.put(`/prodsitefield-requests/${prodsiteId}/submit`)
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      await queryClient.invalidateQueries({
        queryKey: ["productionSiteFieldRequests"],
      })
    },
  })
}

export const useReopenProdsiteFieldRequestMutation = (
  prodsiteId: string,
  options?: UseMutationOptions<void, unknown>
) => {
  return useMutation({
    ...options,
    mutationFn: async () => {
      await httpClient.get(`/admin/prodsitefield-requests/${prodsiteId}/reopen`)
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      await queryClient.invalidateQueries({
        queryKey: ["productionSiteFieldRequests"],
      })
    },
  })
}

export const useTransferProdsiteFieldRequestValuesMutation = (
  options?: UseMutationOptions<
    { count: number },
    AxiosError,
    transferProdsiteFieldValidationType
  >
) => {
  return useMutation({
    ...options,
    mutationFn: async (body) => {
      return (await httpClient.post(`/prodsitefield-requests/transfer`, body))
        .data
    },
    onSuccess: async (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      await queryClient.invalidateQueries({
        queryKey: ["productionSiteFieldRequests"],
      })
    },
  })
}
